/**
 * `ApiValidationError` lifted from `apps/aegix-sms-react/src/lib/api-client.ts`
 * (the SMS `apiClient`). DRF returns 400s with field-keyed string-array
 * payloads (`{email: ["This field is required"], password: [...]}`) that
 * forms want to render inline. The class wraps them so callers can
 * `instanceof ApiValidationError` instead of duck-typing the response body.
 *
 * Wired into the universal `afterResponse` hook chain so every app gets it
 * automatically — there is no opt-out config.
 */
export class ApiValidationError extends Error {
  readonly fieldErrors: Record<string, string[]>;
  readonly status: number;
  /** The machine-readable code off the body, or `null` for an uncoded rejection. */
  readonly errorCode: string | null;

  constructor(
    fieldErrors: Record<string, string[]>,
    status = 400,
    errorCode: string | null = null,
  ) {
    const messages = Object.entries(fieldErrors)
      .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
      .join('; ');
    super(messages || 'Validation failed');
    this.name = 'ApiValidationError';
    this.fieldErrors = fieldErrors;
    this.status = status;
    this.errorCode = errorCode;
  }
}

/**
 * Inspect a parsed 400 response body for the DRF field-error shape and
 * return a typed exception when it matches. Returns null on any other
 * shape so the caller can fall back to a generic error.
 *
 * Accepts both the raw payload (`{field: [errors]}`) and the
 * `{errors: {field: [errors]}}` envelope SMS uses for some endpoints. The
 * top-level `error_code`, when present, rides along as `errorCode`; it is a
 * string, so it is never mistaken for a field.
 */
export function parseValidationError(
  body: unknown,
  status = 400,
): ApiValidationError | null {
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return null;
  }
  const candidate =
    'errors' in body &&
    body.errors &&
    typeof body.errors === 'object' &&
    !Array.isArray(body.errors)
      ? (body.errors as Record<string, unknown>)
      : (body as Record<string, unknown>);

  const hasFieldErrors = Object.values(candidate).some((v) => Array.isArray(v));
  if (!hasFieldErrors) return null;

  const fieldErrors: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(candidate)) {
    if (Array.isArray(value)) {
      fieldErrors[key] = value.map((v) => String(v));
    }
  }
  const code = (body as Record<string, unknown>)['error_code'];
  return new ApiValidationError(
    fieldErrors,
    status,
    typeof code === 'string' && code ? code : null,
  );
}
